/**
 * Data layer for the Troubleshooting page.
 *
 * Two groups of calls:
 *
 *  REAL — endpoints the admin API serves today. These throw on failure like the
 *  rest of the app and the page shows an error banner.
 *
 *  GAP — endpoints this page needs that do not exist yet. Each is wrapped in
 *  `gapCall`, which turns a 404/405 into `{ status: "unavailable" }` so the UI
 *  can render an honest "not connected yet" card, and honours the sample-data
 *  switch by returning the mockup fixture instead. The proposed contracts are
 *  the TypeScript types in troubleshootingInterfaces.ts and the write-up in
 *  docs/troubleshooting-api-gaps.md. When a backend lands, delete the sample
 *  branch and the wrapper for that call — nothing else should need to change.
 */
import { NetworkTarget, RecordingServerRef } from "./probeTargets";
import { normalizeConnectionError } from "./connectionErrorAdvice";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { createApi } from "../../../utils/createApi";
import { createMonitoringAPICall } from "../../../utils/createMonitoringAPICall";
import { fetchAllWireGuardTunnels } from "../../WireGuardTunnels/fetchWireGuardTunnels";
import {
  AccessibleGroup,
  getAccessibleGroups,
} from "../../WireGuardTunnels/wireGuardGroupUtils";
import { isUnderGroup } from "./useScope";
import { safeGetItem } from "../../../../utils/localStorageUtils";
import {
  RefreshStatus,
  AlarmReceiver,
  AlertReceivedRow,
  AlertsReceived,
  CameraLastAlarmRow,
  CameraLastAlarms,
  CameraLiveness,
  ChangesWindow,
  SettingsCompare,
  SiteChanges,
  SiteMarkers,
  CredentialCheckRequest,
  CredentialCheckResponse,
  GapResult,
  PortCheckRequest,
  PortCheckResponse,
  PortProbeResult,
  ParentAccount,
  ProbeOrigin,
  ReceiverTestRecord,
  SiteCameraTestRow,
  SiteCameraTestRun,
  NetworkPathKind,
  NetworkPathStatus,
  SiteConnectionDetail,
  SiteToSiteCheckResult,
  SiteToSiteClassification,
  SiteToSiteConnection,
  SiteToSiteRungId,
  TunnelState,
  SmtpFilenamesResponse,
  SmtpInstructions,
  SmtpRecentResponse,
  TroubleshootingSite,
  WireGuardPeerTelemetry,
  WireGuardTunnel,
} from "../troubleshootingInterfaces";
import {
  SAMPLE_ALERTS_RECEIVED,
  SAMPLE_CAMERA_LAST_ALARMS,
  SAMPLE_CAMERA_LIVENESS,
  SAMPLE_SITE_CHANGES,
  SAMPLE_SETTINGS_COMPARE,
  SAMPLE_CREDENTIAL_CHECK,
  SAMPLE_PORT_CHECK,
  SAMPLE_RECEIVERS,
  SAMPLE_RECEIVER_TESTS,
  SAMPLE_S2S_CHECK,
  SAMPLE_SITE_TO_SITE,
  SAMPLE_SMTP_FILENAMES,
  SAMPLE_SMTP_RECENT_MISSING,
  SAMPLE_SMTP_RECENT_RECEIVED,
  SAMPLE_TELEMETRY,
} from "./troubleshootingSampleData";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import {
  changesQuery,
  DEFAULT_CHANGES_WINDOW,
  windowHoursOf,
} from "./changesWindow";

import {
  SESSION_EXPIRED_MESSAGE,
  isAuthFailure,
  withSessionRecovery,
} from "./authRecovery";

export { fetchAllWireGuardTunnels };

// The axios instance is built inside the closure on purpose: after a session
// refresh the retry must carry the new bearer token, not the one captured
// when the function started. Optional arguments are forwarded only when
// given so call shapes stay exactly what callers wrote.
const apiGet = <T>(url: string, config?: AxiosRequestConfig) =>
  withSessionRecovery(() =>
    config === undefined
      ? createApi("").get<T>(url)
      : createApi("").get<T>(url, config)
  );
const apiPost = <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
  withSessionRecovery(() =>
    config === undefined
      ? createApi("").post<T>(url, body)
      : createApi("").post<T>(url, body, config)
  );
const apiPatch = <T>(url: string, body?: unknown) =>
  withSessionRecovery(() => createApi("").patch<T>(url, body));
const monGet = <T>(url: string, config?: AxiosRequestConfig) =>
  withSessionRecovery(() =>
    config === undefined
      ? createMonitoringAPICall("").get<T>(url)
      : createMonitoringAPICall("").get<T>(url, config)
  );

// ---------------------------------------------------------------------------
// REAL endpoints
// ---------------------------------------------------------------------------

type ParentAccountRow = ParentAccount;

/**
 * The groups the scope bar offers — the same list as the Alerts page's
 * "Parent Group" filter: `group/?parent_account=true`, cached at login as
 * `parentAccounts`, in the API's order. Falls back to the full group cache
 * filtered on the flag, then to the API, when the cache is cold.
 * `customer/names/{id}/` for such a group returns every site under it,
 * subgroups included.
 */
export const fetchParentAccounts = async (): Promise<ParentAccountRow[]> => {
  const rows = (list: { id?: unknown; name?: unknown }[]): ParentAccountRow[] =>
    list
      .filter((g) => typeof g?.id === "number" && Boolean(g?.name))
      .map((g) => ({ id: g.id as number, name: String(g.name) }));
  const cached = rows(
    safeGetItem<ParentAccountRow[]>("parentAccounts", []) ?? []
  );
  if (cached.length > 0) return cached;
  const flagged = getAccessibleGroups().filter((group) => group.parent_account);
  if (flagged.length > 0) return rows(flagged);
  const response = await apiGet<{ results?: ParentAccountRow[] }>(
    "group/?parent_account=true&page_size=10000"
  );
  return rows(response.data?.results ?? []);
};

/**
 * Groups that have an SMTP site — `group/?group_type=smtp`, the list the
 * Clips page offers — so the SMTP tab's group picker skips the rest. Follows
 * pagination like Clips does.
 */
export const fetchSmtpAccountIds = async (): Promise<number[]> => {
  const ids: number[] = [];
  let next: string | null = "group/?group_type=smtp&page_size=10000";
  const seen = new Set<string>();
  while (next && !seen.has(next)) {
    seen.add(next);
    const response: AxiosResponse<{
      results?: { id: number }[];
      next?: string | null;
    }> = await apiGet(next);
    (response.data?.results ?? []).forEach((row) => ids.push(Number(row.id)));
    next = response.data?.next ? relativePollPath(response.data.next) : null;
  }
  return ids;
};

/**
 * How many `customer/names/{groupId}/` requests run at once.
 *
 * There is no "every site I can see" endpoint, so the list is one request per
 * parent account. A customer has a handful; staff have every account (~1,200),
 * and firing them all at once put ~2,350 requests on the API per page load,
 * repeated every minute by the auto refresh.
 */
export const SITE_LOOKUP_CONCURRENCY = 6;

/** The site list rarely changes; health data is what the minute timer is for. */
export const SITE_LOOKUP_TTL_MS = 10 * 60_000;

/** `Promise.all` over `items`, but never more than `limit` calls in flight. */
export const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await fn(items[index]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker)
  );
  return results;
};

export interface TroubleshootingSitesResult {
  sites: TroubleshootingSite[];
  /** The parent accounts the sites were listed under, for the scope picker. */
  accounts: ParentAccount[];
  accountsTotal: number;
  /** Accounts whose site list could not be loaded. Non-zero means `sites` is incomplete. */
  accountsFailed: number;
  /**
   * Why the first failing request failed — "HTTP 403 Forbidden: <detail>",
   * "timeout of 30000ms exceeded", "Network Error" — so the page can say it
   * instead of a bare "could not load". Null when nothing failed.
   */
  failureDetail: string | null;
}

/** One line a person can act on from an axios error: status + server detail, else the message. */
export const describeRequestFailure = (error: unknown): string => {
  if (isAuthFailure(error)) return SESSION_EXPIRED_MESSAGE;
  const err = error as AxiosError | undefined;
  const status = err?.response?.status;
  const detail = serverMessage(error);
  if (status) {
    const text = err?.response?.statusText;
    return `HTTP ${status}${text ? ` ${text}` : ""}${
      detail ? `: ${detail}` : ""
    }`;
  }
  return detail || err?.message || "request failed";
};

interface LookupCache<T> {
  key: string;
  at: number;
  value: T;
}
let sitesCache: LookupCache<TroubleshootingSitesResult> | null = null;
let smtpIdsCache: LookupCache<SmtpSiteIdsResult> | null = null;

/** Test hook, and what "Run all checks" calls so a manual run always re-reads. */
export const clearSiteLookupCache = () => {
  sitesCache = null;
  smtpIdsCache = null;
};

const fresh = <T>(cache: LookupCache<T> | null, key: string): T | null =>
  cache && cache.key === key && Date.now() - cache.at < SITE_LOOKUP_TTL_MS
    ? cache.value
    : null;

/**
 * The sites under `accounts` — by default every parent account the user can
 * see — plus how many accounts failed. `GET customer/names/{groupId}/`, at
 * most `SITE_LOOKUP_CONCURRENCY` at a time, cached for `SITE_LOOKUP_TTL_MS`.
 * Only a complete result is cached, so a partial one is retried next refresh.
 *
 * The page passes the one chosen group: a group is required before anything
 * is checked, so there is no reason to list every account's sites up front —
 * for staff that was 2 × ~1,200 requests per load (ENG-1801).
 */
export const fetchTroubleshootingSitesResult = async (
  onlyAccounts?: ParentAccount[]
): Promise<TroubleshootingSitesResult> => {
  const accounts = onlyAccounts ?? (await fetchParentAccounts());
  const key = accounts
    .map((account) => account.id)
    .sort((a, b) => a - b)
    .join(",");
  const cached = fresh(sitesCache, key);
  if (cached) return cached;

  let accountsFailed = 0;
  let failureDetail: string | null = null;
  const perAccount = await mapWithConcurrency(
    accounts,
    SITE_LOOKUP_CONCURRENCY,
    async (account) => {
      try {
        const response = await apiGet<{ id: number | string; name: string }[]>(
          `customer/names/${account.id}/`,
          { timeout: 60_000 }
        );
        const rows = response.data;
        if (!Array.isArray(rows))
          throw new Error(
            `unexpected response shape (${typeof rows}) from customer/names/${
              account.id
            }/`
          );
        return rows.map((row) => ({
          id: Number(row.id),
          name: row.name,
          groupId: account.id,
        }));
      } catch (error) {
        // One account failing should not blank the whole picker — but it is
        // counted, and the first reason kept, so the page can say what
        // happened rather than "0 sites".
        accountsFailed += 1;
        failureDetail = failureDetail ?? describeRequestFailure(error);
        // eslint-disable-next-line no-console
        console.warn(
          `Troubleshooter: customer/names/${
            account.id
          }/ failed: ${describeRequestFailure(error)}`
        );
        return [] as TroubleshootingSite[];
      }
    }
  );
  const seen = new Set<number>();
  const sites = perAccount
    .flat()
    .filter((site) => {
      if (seen.has(site.id)) return false;
      seen.add(site.id);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
  const result = {
    sites,
    accounts,
    accountsTotal: accounts.length,
    accountsFailed,
    failureDetail,
  };
  if (accountsFailed === 0) sitesCache = { key, at: Date.now(), value: result };
  return result;
};

/** The sites alone; see `fetchTroubleshootingSitesResult` for the failure count. */
export const fetchTroubleshootingSites = async (): Promise<
  TroubleshootingSite[]
> => (await fetchTroubleshootingSitesResult()).sites;

export interface SmtpSiteIdsResult {
  ids: number[];
  groupsFailed: number;
}

/** Ids of SMTP sites. `GET customer/names/{groupId}/?group_type=smtp` (used by Clips today). */
export const fetchSmtpSiteIdsResult = async (
  groupIds: number[]
): Promise<SmtpSiteIdsResult> => {
  const key = [...groupIds].sort((a, b) => a - b).join(",");
  const cached = fresh(smtpIdsCache, key);
  if (cached) return cached;

  let groupsFailed = 0;
  const perGroup = await mapWithConcurrency(
    groupIds,
    SITE_LOOKUP_CONCURRENCY,
    async (groupId) => {
      try {
        const response = await apiGet<{ id: number | string }[]>(
          `customer/names/${groupId}/?group_type=smtp`
        );
        return (response.data ?? []).map((row) => Number(row.id));
      } catch {
        groupsFailed += 1;
        return [] as number[];
      }
    }
  );
  const result = { ids: Array.from(new Set(perGroup.flat())), groupsFailed };
  if (groupsFailed === 0) smtpIdsCache = { key, at: Date.now(), value: result };
  return result;
};

export const fetchSmtpSiteIds = async (groupIds: number[]): Promise<number[]> =>
  (await fetchSmtpSiteIdsResult(groupIds)).ids;

/**
 * Ids of the group's sites with a WireGuard gateway (`Customer.wireguard`),
 * the same test the scope note uses for "connects via WireGuard" (QA SC-16,
 * ENG-2187). `customer/names/{groupId}/?uses_wireguard=` (admin #3213).
 *
 * An admin without the filter ignores the parameter and returns every site
 * for both values, so the two answers are only trusted when they do not
 * overlap; otherwise, or on any error, this returns null (unknown) and the
 * picker keeps using tunnel path rows alone.
 */
export const fetchWireGuardGatewaySiteIds = async (
  groupId: number
): Promise<number[] | null> => {
  try {
    const [withGateway, without] = await Promise.all(
      ["true", "false"].map((value) =>
        apiGet<{ id: number | string }[]>(
          `customer/names/${groupId}/?uses_wireguard=${value}`
        )
      )
    );
    const yes = (withGateway.data ?? []).map((row) => Number(row.id));
    const no = new Set((without.data ?? []).map((row) => Number(row.id)));
    if (yes.some((id) => no.has(id))) return null;
    return yes;
  } catch {
    return null;
  }
};

/** `GET customer/{id}/about/` — how one site connects. */
export const fetchSiteConnectionDetail = async (
  siteId: number
): Promise<SiteConnectionDetail> => {
  const response = await apiGet<SiteConnectionDetail>(
    `customer/${siteId}/about/`
  );
  return response.data;
};

/**
 * `GET camera/{id}/general_info/` — only the saved login. admin masks the
 * password for users without view_passwords; callers must still check.
 */
export const fetchCameraLogin = async (
  cameraId: number
): Promise<{ username: string | null; password: string | null }> => {
  const response = await apiGet<{
    username?: string | null;
    password?: string | null;
  }>(`camera/${cameraId}/general_info/`);
  return {
    username: response.data?.username ?? null,
    password: response.data?.password ?? null,
  };
};

/**
 * Test saved login (ENG-2065): start connector-tools' camera-list job for a
 * site. It logs in to the site's VMS (or vendor cloud) with the saved login,
 * the way the connector does, and reports on the site's status row. admin
 * answers 200 with Django messages; a warning means it did not start (a
 * refresh is already running, or the job could not be dispatched).
 */
export const startSavedLoginTest = (
  siteId: number
): Promise<GapResult<{ refusedMessage: string | null }>> =>
  gapCall(
    `POST customer/${siteId}/action/refresh_camera_list/`,
    "This environment does not offer the camera-list refresh.",
    async () => {
      const response = await apiPost<
        { message?: string | null; level_tag?: string | null }[]
      >(`customer/${siteId}/action/refresh_camera_list/`);
      const warning = (Array.isArray(response.data) ? response.data : []).find(
        (m) => m?.level_tag === "warning" || m?.level_tag === "error"
      );
      return { refusedMessage: warning?.message ?? null };
    },
    null
  );

/** The refresh fields of `GET site_status/?customer=<id>` (epoch seconds). */
export const fetchSavedLoginStatus = async (
  siteId: number
): Promise<RefreshStatus | null> => {
  const response = await apiGet<{ results?: RefreshStatus[] }>(
    `site_status/?customer=${siteId}`
  );
  const rows = response.data?.results ?? [];
  // The site's own row, not a flex schedule's.
  return rows.find((row) => row.flex_schedule == null) ?? rows[0] ?? null;
};

/** Wire shape of one `GET network-targets/` row. */
interface ApiNetworkTarget {
  address: string;
  ports?: number[] | null;
  label?: string | null;
  site_id?: number | null;
  site_name?: string | null;
  camera_id?: number | null;
  source: NetworkTarget["source"];
  tunnel_id?: number | null;
  /** Whether the Default path reaches it for this user (ENG-2055). */
  default_path?: boolean | null;
}

/**
 * The caller's own probe targets (camera addresses, site servers, tunnel
 * subnets), searched by address prefix, site or camera name. admin scopes
 * it by access; customers may only aim private-address probes at these.
 */
export const fetchNetworkTargets = (
  q: string,
  sampleData: boolean
): Promise<GapResult<NetworkTarget[]>> =>
  gapCall(
    "GET network-targets/",
    "The address list comes from actuate_admin's network-targets/ (ENG-2039); this environment does not have it yet.",
    async () => {
      const response = await apiGet<
        { results?: ApiNetworkTarget[] } | ApiNetworkTarget[]
      >(`network-targets/?limit=50&q=${encodeURIComponent(q.trim())}`);
      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.results ?? [];
      return rows.map((row) => ({
        address: row.address,
        ports: row.ports ?? [],
        label: row.label || row.address,
        siteId: row.site_id ?? null,
        siteName: row.site_name ?? null,
        cameraId: row.camera_id ?? null,
        source: row.source,
        tunnelId: row.tunnel_id ?? null,
        defaultPath:
          typeof row.default_path === "boolean" ? row.default_path : null,
      }));
    },
    sampleData ? () => [] : null
  );

/** `GET customer/{id}/instructions/` — SMTP server/port/sender the NVR must use. */
export const fetchSmtpInstructions = async (
  siteId: number
): Promise<SmtpInstructions | null> => {
  const response = await apiGet<SmtpInstructions>(
    `customer/${siteId}/instructions/`
  );
  return response.data?.show_instructions ? response.data : null;
};

export interface SiteCamera {
  id: number;
  camera_name: string;
  /** The address the connector dials (camera or NVR); may be a hostname. */
  ip?: string | null;
  /** The camera's own stream / HTTP ports (e.g. RTSP over HTTP on 6007); absent on older admin. */
  rtsp?: number | null;
  http?: number | null;
  /** The Milestone recording server it streams from (ENG-2063); null/absent otherwise. */
  recording_server?: RecordingServerRef | null;
}

/** admin's camera `recording_server`, or null when absent or malformed. */
const mapCameraRecordingServer = (
  value: unknown
): RecordingServerRef | null => {
  if (!value || typeof value !== "object") return null;
  const row = value as { id?: unknown; name?: unknown; server_ip?: unknown };
  const serverIp = typeof row.server_ip === "string" ? row.server_ip : "";
  if (!serverIp.trim()) return null;
  return {
    id: Number(row.id),
    name: (typeof row.name === "string" && row.name) || serverIp,
    server_ip: serverIp,
  };
};

/** Cameras at a site. `GET camera/site/?customer__id={id}&page=n`, paginated. */
export const fetchSiteCameras = async (
  siteId: number
): Promise<SiteCamera[]> => {
  const cameras: SiteCamera[] = [];
  let page = 1;
  // Hard stop so a misbehaving `next` can never loop forever.
  while (page <= 20) {
    const response = await apiGet<{
      results?: {
        id?: number | string;
        camera_name?: string;
        ip?: string | null;
        rtsp?: number | null;
        http?: number | null;
        recording_server?: unknown;
      }[];
      next?: string | null;
    }>(`camera/site/?customer__id=${siteId}&page=${page}`);
    (response.data?.results ?? []).forEach((camera) => {
      if (camera.camera_name) {
        const recordingServer = mapCameraRecordingServer(
          camera.recording_server
        );
        cameras.push({
          id: Number(camera.id),
          camera_name: camera.camera_name,
          ip: camera.ip ?? null,
          ...(camera.rtsp != null ? { rtsp: camera.rtsp } : {}),
          ...(camera.http != null ? { http: camera.http } : {}),
          ...(recordingServer ? { recording_server: recordingServer } : {}),
        });
      }
    });
    if (!response.data?.next) break;
    page += 1;
  }
  return cameras;
};

/** A Milestone recording server serving a site (`recording_server/`). */
export interface RecordingServer {
  id: number;
  name: string;
  server_ip: string;
}

/**
 * The site's Milestone recording servers: linked to the site, or to one of
 * its cameras (admin's `recording_server/?customer=`, not paginated).
 */
export const fetchRecordingServers = async (
  siteId: number
): Promise<RecordingServer[]> => {
  const response = await apiGet<
    { id?: number; name?: string; server_ip?: string }[] | { results?: unknown }
  >(`recording_server/?customer=${siteId}`);
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows
    .filter((row) => row && row.server_ip)
    .map((row) => ({
      id: Number(row.id),
      name: row.name || row.server_ip || "",
      server_ip: String(row.server_ip),
    }));
};

/** Camera names at a site; see `fetchSiteCameras`. */
export const fetchSiteCameraNames = async (siteId: number): Promise<string[]> =>
  (await fetchSiteCameras(siteId)).map((camera) => camera.camera_name);

/** `PATCH customer/{id}/` — the same two fields the site page saves. */
export const saveFilenameRule = async (
  siteId: number,
  enabled: boolean,
  expression: string
): Promise<void> => {
  await apiPatch(`customer/${siteId}/`, {
    read_filename: enabled,
    read_filename_expression: enabled ? expression : "",
  });
};

// ---------------------------------------------------------------------------
// Async jobs: enqueue + poll (network-port-check pattern)
// ---------------------------------------------------------------------------

interface EnqueuedJob {
  task_id: string;
  status: string;
  poll_url: string;
}

interface JobStatus<T> {
  task_id: string;
  status: "pending" | "success" | "failed";
  result?: T;
  error?: string;
  detail?: string | null;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** `poll_url` comes back as `/api/<route>/<id>/`; createApi already has `api/` in its base URL. */
/**
 * A server-supplied URL as the axios instance wants it: the instance's base
 * already ends in `api/`, so drop the host and that prefix. Handles both the
 * `/api/…` form the job endpoints return and the absolute `next` links DRF
 * pagination returns.
 */
const relativePollPath = (url: string) => {
  let path = url;
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      path = `${parsed.pathname}${parsed.search}`;
    } catch {
      path = url;
    }
  }
  return path.replace(/^\/?api\//, "");
};

export const POLL_INTERVAL_MS = 2500;
export const POLL_TIMEOUT_MS = 120_000;

/**
 * Poll a django-q job until it lands. Resolves the result, or throws with the
 * server's failure message. SSM round trips take 5–15 s; the timeout is
 * generous because a gateway probe can legitimately wait on a 3 s connect
 * per port.
 */
export const pollJob = async <T>(pollUrl: string): Promise<T> => {
  const path = relativePollPath(pollUrl);
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const response = await apiGet<JobStatus<T>>(path);
    const body = response.data;
    if (body.status === "success" && body.result !== undefined)
      return body.result;
    if (body.status === "failed") {
      throw new Error(body.detail || body.error || "The check failed.");
    }
    await sleep(POLL_INTERVAL_MS);
  }
  throw new Error("The check did not finish in time. Try again in a moment.");
};

// ---------------------------------------------------------------------------
// Result wrapper for endpoints that may not exist in this environment yet
// ---------------------------------------------------------------------------

const isMissingEndpoint = (error: unknown) => {
  const status = (error as AxiosError)?.response?.status;
  return status === 404 || status === 405 || status === 501;
};

const serverMessage = (error: unknown): string | null => {
  const data = (error as AxiosError)?.response?.data as
    | { error?: string; detail?: string; [key: string]: unknown }
    | undefined;
  if (!data) return null;
  if (typeof data.error === "string") return data.error;
  if (typeof data.detail === "string") return data.detail;
  // DRF field errors: { host: ["…"] }, or { host: "…" } when a view raises
  // ValidationError({field: message}) itself; one level of nesting too.
  const fromValue = (value: unknown): string | null => {
    if (typeof value === "string" && value.trim()) return value;
    if (Array.isArray(value) && value.length) return fromValue(value[0]);
    if (value && typeof value === "object")
      for (const inner of Object.values(value as Record<string, unknown>)) {
        const found =
          typeof inner === "string" || Array.isArray(inner)
            ? fromValue(inner)
            : null;
        if (found) return found;
      }
    return null;
  };
  for (const value of Object.values(data)) {
    const found = fromValue(value);
    if (found) return found;
  }
  return null;
};

/**
 * Seconds a 429 asks us to wait. admin's shape everywhere is
 * `{error, retry_after_seconds}` plus a Retry-After header.
 */
export const retryAfterSeconds = (error: unknown): number | undefined => {
  const response = (error as AxiosError)?.response;
  if (response?.status !== 429) return undefined;
  const body = response.data as
    | { retry_after_seconds?: unknown; retry_after?: unknown }
    | undefined;
  const header = (response.headers as Record<string, unknown> | undefined)?.[
    "retry-after"
  ];
  const raw = body?.retry_after_seconds ?? body?.retry_after ?? header;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 60;
};

/** The named sample scenario picked next to the switch (ENG-2039). */
const sampleScenario = () => useTroubleshootingStore.getState().sampleScenario;

const gapCall = async <T>(
  endpoint: string,
  reason: string,
  call: () => Promise<T>,
  sample: (() => T) | null
): Promise<GapResult<T>> => {
  if (sample) {
    return { status: "ok", data: sample(), sample: true };
  }
  try {
    // Not wrapped in withSessionRecovery: the request helpers inside `call`
    // (apiGet/apiPost/…) already recover per request. Wrapping the whole
    // closure too re-ran it after the inner retry gave up, which for a
    // job like sendReceiverTest or runPortCheck meant a second real POST.
    return { status: "ok", data: await call() };
  } catch (error) {
    if (isMissingEndpoint(error)) {
      return { status: "unavailable", endpoint, reason };
    }
    const message = isAuthFailure(error)
      ? SESSION_EXPIRED_MESSAGE
      : serverMessage(error) ||
        (error as Error)?.message ||
        "The request failed. Try again in a moment.";
    const wait = retryAfterSeconds(error);
    const httpStatus = (error as AxiosError)?.response?.status;
    const refused =
      httpStatus === 400 ||
      httpStatus === 403 ||
      httpStatus === 409 ||
      httpStatus === 429;
    return {
      status: "error",
      endpoint,
      message,
      ...(wait != null ? { retryAfterSeconds: wait } : {}),
      ...(refused ? { refused: true } : {}),
    };
  }
};

// ---------------------------------------------------------------------------
// Site-to-Site — actuate_admin `network_path/` (ENG-1521, admin #2877)
// ---------------------------------------------------------------------------

/** `GET network_path/` row. */
interface ApiNetworkPath {
  id: number;
  name: string;
  kind: NetworkPathKind;
  kind_display: string;
  group: number | null;
  enabled: boolean;
  vpn_connection_id: string | null;
  aws_region: string | null;
  peer_ip: string | null;
  wireguard_tunnel: number | null;
  probe_target: string | null;
  probe_port: number | null;
  probe_label: string | null;
  status: NetworkPathStatus;
  /** Sites that ride the path (ENG-1674, admin #2953). Absent on older admin. */
  customer_ids?: number[];
}

/**
 * The list gives one status per path, not per tunnel, so draw `tunnels_total`
 * rows and light `tunnels_up` of them. Unknown counts become one unknown row
 * so the table still has something to point at.
 */
const tunnelsFromStatus = (
  status: NetworkPathStatus
): SiteToSiteConnection["tunnels"] => {
  const total = status.tunnels_total ?? 1;
  const up = status.tunnels_up ?? 0;
  const known = status.tunnels_total != null && status.tunnels_up != null;
  return Array.from({ length: Math.max(1, total) }, (_, index) => {
    const state: TunnelState = !known ? "UNKNOWN" : index < up ? "UP" : "DOWN";
    return {
      outside_ip: "",
      state,
      status_message:
        state === "UP"
          ? "UP"
          : state === "DOWN"
          ? "IPSEC IS DOWN"
          : status.status === "disabled"
          ? "Path disabled"
          : "No telemetry",
      last_status_change: null,
    };
  });
};

export const networkPathToConnection = (
  path: ApiNetworkPath,
  sites: TroubleshootingSite[]
): SiteToSiteConnection => {
  const groups = getAccessibleGroups();
  const exactIds = path.customer_ids;
  return {
    id: String(path.id),
    name: path.name,
    kind: path.kind,
    customer_gateway_ip: path.peer_ip || "",
    actuate_gateway: [path.kind_display, path.aws_region]
      .filter(Boolean)
      .join(" "),
    status: path.status,
    // A direct path has no tunnels; its glance comes from the probe alone.
    tunnels: path.kind === "direct" ? [] : tunnelsFromStatus(path.status),
    probe_target: path.probe_target,
    probe_port: path.probe_port,
    probe_label: path.probe_label,
    routes: [],
    site_ids: Array.isArray(exactIds)
      ? [...exactIds].sort((a, b) => a - b)
      : sites
          .filter((site) => isUnderGroup(path.group, site.groupId, groups))
          .map((s) => s.id),
    site_ids_exact: Array.isArray(exactIds),
    wireguard_tunnel: path.wireguard_tunnel ?? null,
  };
};

/**
 * Every enabled network path the caller can see, with the list's status
 * glance. The Connections tab renders the Site-to-Site and direct kinds; the
 * WireGuard kind is kept for its `customer_ids`, which say exactly which
 * sites ride each tunnel (the WireGuard tab itself reads the tunnel rows).
 */
export const fetchSiteToSiteConnections = (
  sampleData: boolean,
  sites: TroubleshootingSite[]
): Promise<GapResult<SiteToSiteConnection[]>> =>
  gapCall(
    "GET network_path/",
    "Site-to-Site status comes from actuate_admin's network_path/ (ENG-1521); this environment does not have it yet.",
    async () => {
      const rows: ApiNetworkPath[] = [];
      let next: string | null = "network_path/?page_size=200";
      const seen = new Set<string>();
      while (next && !seen.has(next)) {
        seen.add(next);
        const response: AxiosResponse<
          | { results?: ApiNetworkPath[]; next?: string | null }
          | ApiNetworkPath[]
        > = await apiGet(next);
        const data:
          | { results?: ApiNetworkPath[]; next?: string | null }
          | ApiNetworkPath[] = response.data;
        if (Array.isArray(data)) {
          rows.push(...data);
          break;
        }
        rows.push(...(data?.results ?? []));
        next = data?.next ? relativePollPath(data.next) : null;
      }
      return rows
        .filter((path) => path.enabled)
        .map((path) => networkPathToConnection(path, sites));
    },
    sampleData
      ? () =>
          SAMPLE_SITE_TO_SITE(
            sites.map((site) => site.id),
            sampleScenario()
          )
      : null
  );

/** `GET network_path/{id}/health/` body, the parts this page reads. */
interface ApiNetworkPathHealth {
  steps: {
    id: SiteToSiteRungId;
    title: string;
    status: "pass" | "fail" | "warn" | "skipped";
    summary: string;
    diagnosis?: string | null;
    next_action?: string | null;
    skipped_reason?: string | null;
  }[];
  classification: SiteToSiteClassification;
  checked_at?: string;
}

/**
 * The four-rung ladder for one path, synchronous on the API side (one cached
 * AWS describe plus at most one 3 s TCP connect).
 */
export const runSiteToSiteCheck = (
  connection: SiteToSiteConnection,
  sampleData: boolean
): Promise<GapResult<SiteToSiteCheckResult>> =>
  gapCall(
    `GET network_path/${connection.id}/health/`,
    "The connection check is actuate_admin's network_path/{id}/health/ ladder (ENG-1521); this environment does not have it yet.",
    async () => {
      const response = await apiGet<ApiNetworkPathHealth>(
        `network_path/${connection.id}/health/`,
        { timeout: 30_000 }
      );
      const body = response.data;
      return {
        rungs: (body.steps ?? []).map((step) => ({
          id: step.id,
          title: step.title,
          status: step.status,
          summary:
            step.status === "skipped" && step.skipped_reason
              ? `Skipped: ${step.skipped_reason}`
              : step.summary,
          diagnosis: step.diagnosis ?? null,
          next_action: step.next_action ?? null,
        })),
        classification: body.classification,
        checked_at: body.checked_at ?? null,
      };
    },
    sampleData ? () => SAMPLE_S2S_CHECK(connection) : null
  );

/**
 * Per-peer telemetry the gateway runtime records on every ~10 min run
 * (endpoint, keepalive, transfer deltas, initiations heuristic) — actuate_admin
 * `wireguard_tunnel/telemetry/` (ENG-1668). Tunnels the gateway has not
 * sampled yet arrive with null fields, so a missing row means "no data yet".
 */
export const fetchWireGuardTelemetry = (
  tunnels: WireGuardTunnel[],
  sampleData: boolean
): Promise<GapResult<Record<number, WireGuardPeerTelemetry>>> =>
  gapCall(
    "GET wireguard_tunnel/telemetry/?all_levels=true",
    "Peer telemetry comes from actuate_admin's wireguard_tunnel/telemetry/ (ENG-1668, fed by the gateway runtime); this environment does not have it yet.",
    async () => {
      const response = await apiGet<
        { results?: WireGuardPeerTelemetry[] } | WireGuardPeerTelemetry[]
      >("wireguard_tunnel/telemetry/?all_levels=true");
      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.results ?? [];
      const out: Record<number, WireGuardPeerTelemetry> = {};
      rows.forEach((row) => {
        out[row.tunnel_id] = row;
      });
      return out;
    },
    sampleData ? () => SAMPLE_TELEMETRY(tunnels) : null
  );

// ---------------------------------------------------------------------------
// Alarm receivers — actuate_admin `alarm-receivers/` (ENG-1523, admin PR #2918)
// ---------------------------------------------------------------------------

/** Wire shape of `GET /api/alarm-receivers/`. */
interface ApiAlarmReceiver {
  id: number;
  kind: "immix" | "dice";
  label: string;
  host: string;
  port: number;
  enabled: boolean;
  group_id: number | null;
  site_ids: number[];
  sites_count: number;
  via_connection: AlarmReceiver["via_connection"];
  health: {
    listening: AlarmReceiver["health"]["listening"];
    detail: string;
    last_probe_at: string | null;
    last_accepted_at: string | null;
    last_error_at: string | null;
    last_error: string | null;
    consecutive_failures: number;
    attempts_last_hour: number;
    failures_last_hour: number;
  };
}

interface ApiReceiverTest {
  id: number;
  receiver_id: number;
  token: string;
  send_to: string | null;
  sent_at: string;
  result: "pending" | "delivered" | "failed";
  detail: string | null;
  duration_ms: number | null;
  completed_at: string | null;
  sent_by: string;
}

const mapReceiver = (row: ApiAlarmReceiver): AlarmReceiver => ({
  id: String(row.id),
  type: row.kind,
  label: row.label,
  host: row.host,
  port: row.port,
  via_connection: row.via_connection ?? null,
  site_ids: row.site_ids ?? [],
  health: {
    listening: row.health?.listening ?? "unknown",
    detail: row.health?.detail ?? "Not checked yet",
    last_accepted_at: row.health?.last_accepted_at ?? null,
    failures_last_hour: row.health?.failures_last_hour ?? null,
  },
});

/**
 * Receiver outcome vocabulary (ENG-1523): "Accepted by receiver" — an SMTP 250
 * is acceptance, not delivery to an operator's screen, so never "Delivered".
 * A failure is "Unreachable" when nothing answered (timeout, refused socket,
 * no route, DNS) and "Refused" when the receiver answered and said no; when
 * the detail does not say which, the neutral "Not accepted" is used.
 */
export const receiverOutcomeLabel = (
  result: ReceiverTestRecord["result"],
  detail: string | null | undefined
): string => {
  if (result === "delivered") return "Accepted by receiver";
  if (result === "pending") return "Pending";
  const text = detail || "";
  if (
    /timed?\s?out|unreachable|no route|could not connect|connection (refused|reset)|dns|resolve/i.test(
      text
    )
  )
    return "Unreachable";
  if (/\b[45]\d\d\b|reject|refus|denied|not accepted/i.test(text))
    return "Refused";
  return "Not accepted";
};

const mapReceiverTest = (row: ApiReceiverTest): ReceiverTestRecord => ({
  id: String(row.id),
  receiver_id: String(row.receiver_id),
  sent_at: row.sent_at,
  result: row.result,
  detail:
    row.detail ||
    (row.result === "pending"
      ? "Sent; waiting for the receiver to report"
      : receiverOutcomeLabel(row.result, null)),
  sent_by: row.sent_by || "you",
});

/** Receivers the caller can see, with listener health and their own sites. */
export const fetchAlarmReceivers = (
  sampleData: boolean,
  siteIds: number[]
): Promise<GapResult<AlarmReceiver[]>> =>
  gapCall(
    "GET alarm-receivers/",
    "Alarm receiver rows come from actuate_admin PR #2918 (ENG-1523); this environment does not have it yet.",
    async () => {
      const response = await apiGet<
        { results?: ApiAlarmReceiver[] } | ApiAlarmReceiver[]
      >("alarm-receivers/");
      const data = response.data;
      const rows = Array.isArray(data) ? data : data?.results ?? [];
      return rows.map(mapReceiver);
    },
    sampleData ? () => SAMPLE_RECEIVERS(siteIds, sampleScenario()) : null
  );

/** Recent test alarms for the caller's receivers. */
export const fetchReceiverTests = (
  sampleData: boolean,
  receiverId?: string,
  limit = 20
): Promise<GapResult<ReceiverTestRecord[]>> =>
  gapCall(
    "GET alarm-receivers/tests/",
    "Test alarm history comes from actuate_admin PR #2918 (ENG-1523); this environment does not have it yet.",
    async () => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (receiverId) params.set("receiver", receiverId);
      const response = await apiGet<
        { results?: ApiReceiverTest[] } | ApiReceiverTest[]
      >(`alarm-receivers/tests/?${params.toString()}`);
      const data = response.data;
      const rows = Array.isArray(data) ? data : data?.results ?? [];
      return rows.map(mapReceiverTest);
    },
    sampleData ? () => SAMPLE_RECEIVER_TESTS(sampleScenario()) : null
  );

export const TEST_ALARM_POLL_MS = 3000;
export const TEST_ALARM_WAIT_MS = 45_000;

/**
 * Send one test alarm through the real consumer and wait for its outcome.
 *
 * The POST answers 202 with a `pending` row; the consumer reports the
 * delivery back to admin asynchronously, so we poll the test history for up
 * to 45 s. A row still `pending` after that is returned as-is — the UI says
 * so rather than inventing a result.
 */
export const sendReceiverTest = (
  receiver: AlarmReceiver,
  sampleData: boolean
): Promise<GapResult<ReceiverTestRecord>> =>
  gapCall(
    `POST alarm-receivers/${receiver.id}/test/`,
    "Receiver test alarms come from actuate_admin PR #2918 (ENG-1523); this environment does not have it yet.",
    async () => {
      const response = await apiPost<ApiReceiverTest>(
        `alarm-receivers/${receiver.id}/test/`,
        {}
      );
      let record = mapReceiverTest(response.data);
      const deadline = Date.now() + TEST_ALARM_WAIT_MS;
      while (record.result === "pending" && Date.now() < deadline) {
        await sleep(TEST_ALARM_POLL_MS);
        const latest = await apiGet<ApiReceiverTest[]>(
          `alarm-receivers/tests/?receiver=${receiver.id}&limit=5`
        );
        const match = (latest.data ?? []).find(
          (row) => String(row.id) === record.id
        );
        if (match) record = mapReceiverTest(match);
      }
      return record;
    },
    sampleData
      ? () => {
          const failed = receiver.health.listening !== "listening";
          return {
            id: `sample-${Date.now()}`,
            receiver_id: receiver.id,
            sent_at: new Date().toISOString(),
            result: failed ? "failed" : "delivered",
            detail: failed
              ? `${receiver.health.detail} connecting to ${receiver.host}:${receiver.port}`
              : `Accepted in 410 ms`,
            sent_by: "you",
          };
        }
      : null
  );

// ---------------------------------------------------------------------------
// Per-camera site test — actuate_admin `alarm-receivers/test-site/{site}/`
// (endpoint: ENG-1921, admin #2965; UI: ENG-2633)
// ---------------------------------------------------------------------------

interface ApiSiteTestRow extends ApiReceiverTest {
  camera_id: number | null;
  camera_name: string | null;
  batch_id: string | null;
}

interface ApiSiteTestResult {
  customer_id: number;
  batch_id: string;
  sent: number;
  tests: ApiSiteTestRow[];
  skipped: { camera_id: number; camera_name: string; reason: string }[];
}

/** `tests/?batch=` returns at most this many rows; admin caps a run at 200. */
export const SITE_TEST_POLL_LIMIT = 100;

const mapSiteTestRow = (row: ApiSiteTestRow): SiteCameraTestRow => ({
  testId: String(row.id),
  cameraId: row.camera_id ?? 0,
  cameraName: row.camera_name || `Camera ${row.camera_id ?? "?"}`,
  receiverId: String(row.receiver_id),
  sentAt: row.sent_at,
  result: row.result,
  detail:
    row.detail ||
    (row.result === "pending"
      ? "Sent; waiting for the receiver to report"
      : receiverOutcomeLabel(row.result, null)),
});

const hasPending = (rows: SiteCameraTestRow[]) =>
  rows.some((row) => row.result === "pending");

/**
 * Send one test alarm per camera at a site. `cameraIds` limits the run (e.g.
 * the cameras with no alarm in 48 h); null tests every camera with an active
 * Immix/Dice alert. Answers 202 with `pending` rows; poll
 * fetchSiteCameraTestOutcomes for results. The server refuses with 400 (no
 * testable camera), 409 (test alarms unavailable here), 413 (over the per-run
 * cap) or 429 (site cooldown, with Retry-After); gapCall carries its message
 * and `retryAfterSeconds` through.
 */
export const startSiteCameraTest = (
  siteId: number,
  mode: SiteCameraTestRun["mode"],
  cameraIds: number[] | null,
  sampleData: boolean,
  sampleCameras: { cameraPk: number; cameraName: string }[] = []
): Promise<GapResult<SiteCameraTestRun>> =>
  gapCall(
    `POST alarm-receivers/test-site/${siteId}/`,
    "Per-camera test alarms come from actuate_admin #2965 (ENG-1921); this environment does not have it yet.",
    async () => {
      const response = await apiPost<ApiSiteTestResult>(
        `alarm-receivers/test-site/${siteId}/`,
        cameraIds ? { camera_ids: cameraIds } : {}
      );
      const rows = (response.data.tests ?? []).map(mapSiteTestRow);
      return {
        siteId,
        batchId: String(response.data.batch_id),
        mode,
        sentAt: rows[0]?.sentAt ?? new Date().toISOString(),
        rows,
        skipped: (response.data.skipped ?? []).map((row) => ({
          cameraId: row.camera_id,
          cameraName: row.camera_name,
          reason: row.reason,
        })),
        polling: hasPending(rows),
      };
    },
    sampleData
      ? () => {
          const sentAt = new Date().toISOString();
          const rows: SiteCameraTestRow[] = sampleCameras.map((cam, i) => {
            const failed = i % 4 === 3;
            return {
              testId: `sample-${cam.cameraPk}`,
              cameraId: cam.cameraPk,
              cameraName: cam.cameraName,
              receiverId: "sample",
              sentAt,
              result: failed ? "failed" : "delivered",
              detail: failed
                ? "Timed out connecting to receiver"
                : "Accepted in 410 ms",
            };
          });
          return {
            siteId,
            batchId: `sample-${Date.now()}`,
            mode,
            sentAt,
            rows,
            skipped: [],
            polling: false,
          };
        }
      : null
  );

/**
 * One poll of a run's outcomes (`tests/?batch=`). Rows the page did not
 * return (a run over SITE_TEST_POLL_LIMIT) keep their last known state.
 * Errors are left to the caller, which keeps the run as it was.
 */
export const fetchSiteCameraTestOutcomes = async (
  run: SiteCameraTestRun
): Promise<SiteCameraTestRun> => {
  const params = new URLSearchParams({
    batch: run.batchId,
    limit: String(SITE_TEST_POLL_LIMIT),
  });
  const response = await apiGet<
    { results?: ApiSiteTestRow[] } | ApiSiteTestRow[]
  >(`alarm-receivers/tests/?${params.toString()}`);
  const data = response.data;
  const latest = Array.isArray(data) ? data : data?.results ?? [];
  const byId = new Map(
    latest.map((row) => [String(row.id), mapSiteTestRow(row)])
  );
  const rows = run.rows.map((row) => byId.get(row.testId) ?? row);
  return { ...run, rows, polling: hasPending(rows) };
};

/**
 * TCP-probe the receiver's host:port from the NAT proxy. Resolves when the
 * probe has landed on the receiver's status row; callers then re-fetch the
 * receiver list to show the new `listening`.
 */
export const probeReceiver = (
  receiver: AlarmReceiver,
  sampleData: boolean
): Promise<GapResult<{ result: PortProbeResult; ms: number | null }>> =>
  gapCall(
    `POST alarm-receivers/${receiver.id}/probe/`,
    "Receiver probes come from actuate_admin PR #2918 (ENG-1523); this environment does not have it yet.",
    async () => {
      const response = await apiPost<EnqueuedJob>(
        `alarm-receivers/${receiver.id}/probe/`,
        {}
      );
      const payload = await pollJob<ApiPortCheckResult>(response.data.poll_url);
      const row = payload.results.find((r) => r.port === receiver.port);
      return { result: row?.result ?? "error", ms: row?.ms ?? null };
    },
    sampleData
      ? () => ({
          result:
            receiver.health.listening === "listening"
              ? ("open" as const)
              : ("filtered" as const),
          ms: receiver.health.listening === "listening" ? 14 : null,
        })
      : null
  );

// ---------------------------------------------------------------------------
// Connectivity analyzer — actuate_admin `network-port-check/` (ENG-1669, admin PR #2917)
// ---------------------------------------------------------------------------

/** Wire shape of a landed `network-port-check` job result. */
interface ApiPortCheckResult {
  host: string;
  resolved_ip: string | null;
  from_label: string;
  ping: {
    available: boolean;
    reply: boolean | null;
    ms: number | null;
  } | null;
  results: {
    port: number;
    result: PortProbeResult;
    ms: number | null;
    detail: string | null;
  }[];
  checked_at: string;
  error?: string | null;
}

const originForApi = (from: ProbeOrigin) => {
  if (from.kind === "wireguard")
    return { kind: "wireguard", tunnel_id: from.tunnel_id };
  if (from.kind === "site-to-site")
    return {
      kind: "site_to_site",
      network_path_id: Number(from.connection_id) || null,
    };
  return { kind: "nat" };
};

/**
 * Probe a host from a chosen origin. Enqueue + poll: every origin is an SSM
 * round trip on the server. Site-to-Site origins are refused by the server
 * until NetworkPath (ENG-1521) lands; the message comes back verbatim.
 */
export const runPortCheck = (
  request: PortCheckRequest,
  fromLabel: string,
  sampleData: boolean
): Promise<GapResult<PortCheckResponse>> =>
  gapCall(
    "POST network-port-check/",
    "The connectivity analyzer comes from actuate_admin PR #2917 (ENG-1669); this environment does not have it yet.",
    async (): Promise<PortCheckResponse> => {
      const response = await apiPost<EnqueuedJob>("network-port-check/", {
        host: request.host,
        ports: request.ports,
        origin: originForApi(request.from),
        ping: true,
      });
      const payload = await pollJob<ApiPortCheckResult>(response.data.poll_url);
      return {
        host: payload.host,
        from_label: payload.from_label || fromLabel,
        ping:
          payload.ping && payload.ping.available && payload.ping.reply !== null
            ? { reply: Boolean(payload.ping.reply), ms: payload.ping.ms }
            : null,
        results: payload.results.map((r) => ({
          port: r.port,
          result: r.result,
          ms: r.ms,
          detail: r.detail ?? null,
        })),
        checked_at: payload.checked_at,
        resolved_ip: payload.resolved_ip ?? null,
      };
    },
    sampleData
      ? () => SAMPLE_PORT_CHECK(request, fromLabel, sampleScenario())
      : null
  );

interface ApiCredentialCheckResult {
  host: string;
  resolved_ip: string | null;
  from_label: string;
  tcp: { ok: boolean; ms: number | null; detail?: string | null } | null;
  onvif: { ok: boolean; http_status: number | null; detail: string } | null;
  rtsp: { ok: boolean; rtsp_status: number | null; detail: string } | null;
  device: { manufacturer?: string; model?: string; firmware?: string } | null;
  checked_at: string;
  attempts_remaining?: number;
}

/**
 * ONVIF GetDeviceInformation + RTSP DESCRIBE with the credentials the customer
 * typed, from the NAT proxy or the site's WireGuard gateway. Synchronous: the
 * API waits for the probe (5–15 s) so the password never sits in a job row.
 * A 429 (per-host attempt cap) or 502 (probe could not run) surfaces as the
 * API's own message through `GapResult.error`.
 */
export const runCredentialCheck = (
  request: CredentialCheckRequest,
  sampleData: boolean
): Promise<GapResult<CredentialCheckResponse>> =>
  gapCall(
    "POST network-credential-check/",
    "The credential test comes from actuate_admin PR #2928 (ENG-1670); this environment does not have it yet.",
    async (): Promise<CredentialCheckResponse> => {
      const response = await apiPost<ApiCredentialCheckResult>(
        "network-credential-check/",
        {
          host: request.host,
          port: request.port,
          rtsp_port: request.rtsp_port ?? 554,
          username: request.username,
          password: request.password,
          origin: originForApi(request.from),
        },
        // The probe itself takes 5–15 s; leave headroom over the default.
        { timeout: 60_000 }
      );
      const payload = response.data;
      return {
        tcp: payload.tcp
          ? { ok: payload.tcp.ok, ms: payload.tcp.ms }
          : { ok: false, ms: null },
        onvif: payload.onvif ?? {
          ok: false,
          http_status: null,
          detail: "not attempted",
        },
        rtsp: payload.rtsp ?? {
          ok: false,
          rtsp_status: null,
          detail: "not attempted",
        },
        device: payload.device ?? undefined,
        from_label: payload.from_label,
        attempts_remaining: payload.attempts_remaining,
        resolved_ip: payload.resolved_ip ?? null,
        checked_at: payload.checked_at,
      };
    },
    sampleData
      ? () => SAMPLE_CREDENTIAL_CHECK(request.password, sampleScenario())
      : null
  );

// ---------------------------------------------------------------------------
// SMTP — actuate_admin `customer/{id}/smtp_filenames/` + `smtp_recent/` (ENG-1671/1672, admin PR #2919)
// ---------------------------------------------------------------------------

export const SMTP_FILENAMES_WINDOW_HOURS = 12;

interface ApiSmtpFilenamesResponse {
  read_filename: boolean;
  read_filename_expression: string;
  suggested_expression: string | null;
  samples: {
    filename: string;
    extracted_camera_name: string | null;
    last_seen: string | null;
    count?: number;
    suggested_camera_name?: string | null;
    extracted_matches_existing_camera_id?: number | null;
    suggested_matches_existing_camera_id?: number | null;
  }[];
}

/** Recent attachment filenames for a site, last 12 h, with how many emails carried each. */
export const fetchSmtpFilenames = (
  siteId: number,
  sampleData: boolean
): Promise<GapResult<SmtpFilenamesResponse>> =>
  gapCall(
    `GET customer/${siteId}/smtp_filenames/`,
    "Recent filenames come from the SMTP receiver's logs via actuate_admin (PR #2919 adds counts); this environment does not have it yet.",
    async () => {
      const response = await apiGet<ApiSmtpFilenamesResponse>(
        `customer/${siteId}/smtp_filenames/?since_hours=${SMTP_FILENAMES_WINDOW_HOURS}&limit=50`
      );
      const data = response.data;
      return {
        read_filename: Boolean(data.read_filename),
        read_filename_expression: data.read_filename_expression || "",
        suggested_expression: data.suggested_expression || "",
        samples: (data.samples ?? []).map((sample) => ({
          filename: sample.filename,
          count_12h: sample.count ?? undefined,
          last_seen: sample.last_seen ?? null,
          extracted_camera_name: sample.extracted_camera_name ?? null,
          suggested_camera_name: sample.suggested_camera_name ?? null,
          extracted_matches_existing_camera_id:
            sample.extracted_matches_existing_camera_id ?? null,
          suggested_matches_existing_camera_id:
            sample.suggested_matches_existing_camera_id ?? null,
        })),
      };
    },
    sampleData ? () => SAMPLE_SMTP_FILENAMES(sampleScenario()) : null
  );

interface ApiSmtpRecentResponse extends SmtpRecentResponse {
  since: string;
  logs_available: boolean;
}

/**
 * "Did my test email arrive?" — emails the receiver handled for the site
 * since `sinceIso`, plus a fleet-wide receiver heartbeat. `logs_available`
 * false means New Relic did not answer, which is not evidence of silence, so
 * it is surfaced as an error rather than an empty inbox.
 */
export const fetchSmtpRecent = (
  siteId: number,
  sinceIso: string,
  sender: string,
  sampleData: boolean,
  sampleMode: "received" | "missing" = "received"
): Promise<GapResult<SmtpRecentResponse>> =>
  gapCall(
    `GET customer/${siteId}/smtp_recent/`,
    "The test-email listener comes from actuate_admin PR #2919 (ENG-1672); this environment does not have it yet.",
    async () => {
      const response = await apiGet<ApiSmtpRecentResponse>(
        `customer/${siteId}/smtp_recent/?since=${encodeURIComponent(
          sinceIso
        )}&limit=20`
      );
      const data = response.data;
      if (data.logs_available === false) {
        throw new Error(
          "We could not read the mail receiver's logs just now, so we can't tell whether your email arrived. Try again in a minute."
        );
      }
      return {
        emails: data.emails ?? [],
        receiver_accepting: Boolean(data.receiver_accepting),
        receiver_emails_10m: data.receiver_emails_10m ?? null,
        last_email_from_site: data.last_email_from_site ?? null,
      };
    },
    sampleData
      ? () =>
          sampleMode === "received"
            ? SAMPLE_SMTP_RECENT_RECEIVED(sender)
            : SAMPLE_SMTP_RECENT_MISSING
      : null
  );

// ---------------------------------------------------------------------------
// Alerts received — monitoring-api `alert/` (ENG-1902)
// ---------------------------------------------------------------------------

export const ALERTS_RECEIVED_WINDOW_HOURS = 1;
/** Enough for a busy account's hour; the card wants a count and the newest row. */
const ALERTS_RECEIVED_LIMIT = 2000;

/** The monitoring API's alert record, the fields this page reads. */
interface ApiAlertRecord {
  approx_capture_timestamp: string | number;
  display_name: string;
  camera_name: string;
  customer_pk?: number | null;
  camera_pk?: number | null;
}

/** Epoch seconds (the API's `approx_capture_timestamp`) or ms → ISO. */
export const alertTimestampToIso = (raw: string | number): string | null => {
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return null;
  const ms = value < 1e12 ? value * 1000 : value;
  return new Date(ms).toISOString();
};

export const summariseAlerts = (
  records: ApiAlertRecord[],
  sinceMs: number,
  windowHours: number,
  /** The `limit` the request carried; a full page means the count is a floor. */
  limit = Infinity
): AlertsReceived => {
  const rows: AlertReceivedRow[] = [];
  const latestByCamera: Record<number, string> = {};
  records.forEach((record) => {
    const at = alertTimestampToIso(record.approx_capture_timestamp);
    if (!at || new Date(at).getTime() < sinceMs) return;
    rows.push({
      at,
      siteName: record.display_name || "",
      cameraName: record.camera_name || "",
      sitePk: record.customer_pk ?? null,
      cameraPk: record.camera_pk ?? null,
    });
    if (record.camera_pk != null) {
      const previous = latestByCamera[record.camera_pk];
      if (!previous || previous < at) latestByCamera[record.camera_pk] = at;
    }
  });
  rows.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  return {
    windowHours,
    since: new Date(sinceMs).toISOString(),
    count: rows.length,
    truncated: records.length >= limit,
    latest: rows[0] ?? null,
    camerasAlerted: Object.keys(latestByCamera).length,
    latestByCamera,
  };
};

/**
 * Alerts the monitoring API processed for the scope in the last hour
 * (`start_timestamp` is epoch seconds and overrides every other date
 * parameter). Group scope sends `group_id`; site scope adds `customer_id`.
 */
export const fetchAlertsReceived = (
  groupId: number,
  siteId: number | null,
  siteName: string | null,
  sampleData: boolean
): Promise<GapResult<AlertsReceived>> =>
  gapCall(
    "GET monitoring-api/alert/",
    "Recent alerts come from the monitoring API's alert/ list; this environment does not have it yet.",
    async (): Promise<AlertsReceived> => {
      const sinceMs = Date.now() - ALERTS_RECEIVED_WINDOW_HOURS * 3_600_000;
      const params = new URLSearchParams({
        window_filter: "all",
        start_timestamp: String(Math.floor(sinceMs / 1000)),
        limit: String(ALERTS_RECEIVED_LIMIT),
        group_id: String(groupId),
      });
      if (siteId != null) params.set("customer_id", String(siteId));
      const response = await monGet<{
        records?: ApiAlertRecord[];
      }>(`alert/?${params.toString()}`, { timeout: 30_000 });
      return summariseAlerts(
        response.data?.records ?? [],
        sinceMs,
        ALERTS_RECEIVED_WINDOW_HOURS,
        ALERTS_RECEIVED_LIMIT
      );
    },
    sampleData ? () => SAMPLE_ALERTS_RECEIVED(siteName, sampleScenario()) : null
  );

// ---------------------------------------------------------------------------
// Per-camera last alarm for one site — monitoring-api `alert/` (ENG-1921)
// ---------------------------------------------------------------------------

export const CAMERA_LAST_ALARM_WINDOW_HOURS = 48;
/**
 * One request, newest first. The list endpoint runs the whole query and
 * slices in memory, so cursor pages would re-run it per page; a single large
 * page is cheaper for the API and bounded for the browser. A full page means
 * the oldest alerts were cut, which `truncated` and `since` say.
 */
export const CAMERA_LAST_ALARM_LIMIT = 4000;

/**
 * Join a site's cameras to its recent alerts: one row per camera, newest
 * alert and count, quiet cameras first (then the longest-quiet). A camera that
 * alerted but is no longer on the site list is kept under its alert's name so
 * the count of alerts still adds up.
 */
export const summariseCameraAlarms = (
  siteId: number,
  cameras: SiteCamera[],
  records: ApiAlertRecord[],
  sinceMs: number,
  windowHours: number,
  limit = Infinity
): CameraLastAlarms => {
  const byCamera = new Map<number, CameraLastAlarmRow>();
  cameras.forEach((camera) =>
    byCamera.set(camera.id, {
      cameraPk: camera.id,
      cameraName: camera.camera_name,
      lastAt: null,
      count: 0,
    })
  );
  let oldestSeen: string | null = null;
  records.forEach((record) => {
    const at = alertTimestampToIso(record.approx_capture_timestamp);
    if (!at || Date.parse(at) < sinceMs || record.camera_pk == null) return;
    // The request is filtered by customer_id; another site's row would be an
    // API surprise, and must not make one of its cameras look like ours.
    if (record.customer_pk != null && record.customer_pk !== siteId) return;
    if (!oldestSeen || at < oldestSeen) oldestSeen = at;
    const row = byCamera.get(record.camera_pk) ?? {
      cameraPk: record.camera_pk,
      cameraName: record.camera_name || `camera ${record.camera_pk}`,
      lastAt: null,
      count: 0,
    };
    row.count += 1;
    if (!row.lastAt || row.lastAt < at) row.lastAt = at;
    byCamera.set(record.camera_pk, row);
  });
  const rows = Array.from(byCamera.values()).sort((a, b) => {
    if (a.lastAt == null && b.lastAt != null) return -1;
    if (a.lastAt != null && b.lastAt == null) return 1;
    if (a.lastAt && b.lastAt && a.lastAt !== b.lastAt)
      return a.lastAt < b.lastAt ? -1 : 1;
    return a.cameraName.localeCompare(b.cameraName);
  });
  const truncated = records.length >= limit;
  return {
    siteId,
    windowHours,
    since:
      truncated && oldestSeen ? oldestSeen : new Date(sinceMs).toISOString(),
    rows,
    quiet: rows.filter((row) => row.lastAt == null).length,
    truncated,
  };
};

/**
 * Per-camera last alarm for one site over the last 48 hours: the site's
 * camera list joined to `alert/` for that site (`customer_id`, so no automatic
 * limit applies; ours does).
 */
export const fetchCameraLastAlarms = (
  groupId: number,
  siteId: number,
  sampleData: boolean
): Promise<GapResult<CameraLastAlarms>> =>
  gapCall(
    "GET monitoring-api/alert/",
    "Per-camera alert history comes from the monitoring API's alert/ list; this environment does not have it yet.",
    async (): Promise<CameraLastAlarms> => {
      const sinceMs = Date.now() - CAMERA_LAST_ALARM_WINDOW_HOURS * 3_600_000;
      const params = new URLSearchParams({
        window_filter: "all",
        start_timestamp: String(Math.floor(sinceMs / 1000)),
        limit: String(CAMERA_LAST_ALARM_LIMIT),
        group_id: String(groupId),
        customer_id: String(siteId),
      });
      const [cameras, response] = await Promise.all([
        fetchSiteCameras(siteId),
        monGet<{ records?: ApiAlertRecord[] }>(`alert/?${params.toString()}`, {
          timeout: 60_000,
        }),
      ]);
      return summariseCameraAlarms(
        siteId,
        cameras,
        response.data?.records ?? [],
        sinceMs,
        CAMERA_LAST_ALARM_WINDOW_HOURS,
        CAMERA_LAST_ALARM_LIMIT
      );
    },
    sampleData
      ? () => SAMPLE_CAMERA_LAST_ALARMS(siteId, sampleScenario())
      : null
  );

// ---------------------------------------------------------------------------
// Per-camera "working now" signals — actuate_admin camera_liveness/ (ENG-2046)
// ---------------------------------------------------------------------------

interface ApiCameraLiveness {
  site_id: number;
  checked_at?: string | null;
  signals?: { last_frame?: boolean; bandwidth?: boolean; last_image?: boolean };
  last_frame_source?: string | null;
  connector?: {
    last_seen_at?: string | null;
    census_running?: boolean | null;
    in_schedule?: boolean | null;
  } | null;
  cameras?: {
    camera_id: number;
    camera_name: string;
    host?: string | null;
    continuous?: boolean;
    last_frame_at?: string | null;
    bandwidth_updated_at?: string | null;
    inbound_bandwidth_kbps?: number | null;
    last_image_received_at?: string | null;
    broken_frame?: boolean | null;
    keyframe_interval_seconds?: number | null;
    keyframe_interval_frames?: number | null;
    keyframe_measured_at?: string | null;
    keyframe_interval_is_lower_bound?: boolean | null;
    /** Last failed VMS login (ENG-2252); absent on older admin. */
    last_connection_error?: unknown;
  }[];
  errors?: { source: string; detail: string }[];
  /** Newest scope:"site" VMS login failure (ENG-2252); absent on older admin. */
  connection_error?: unknown;
}

export const mapCameraLiveness = (body: ApiCameraLiveness): CameraLiveness => ({
  siteId: body.site_id,
  checkedAt: body.checked_at ?? null,
  signals: {
    lastFrame: Boolean(body.signals?.last_frame),
    bandwidth: Boolean(body.signals?.bandwidth),
    lastImage: Boolean(body.signals?.last_image),
  },
  lastFrameSource: body.last_frame_source ?? null,
  connector: body.connector
    ? {
        lastSeenAt: body.connector.last_seen_at ?? null,
        censusRunning: body.connector.census_running ?? null,
        inSchedule: body.connector.in_schedule ?? null,
      }
    : null,
  cameras: (body.cameras ?? []).map((camera) => ({
    cameraId: camera.camera_id,
    cameraName: camera.camera_name,
    host: camera.host ?? null,
    continuous: Boolean(camera.continuous),
    lastFrameAt: camera.last_frame_at ?? null,
    bandwidthUpdatedAt: camera.bandwidth_updated_at ?? null,
    inboundKbps: camera.inbound_bandwidth_kbps ?? null,
    lastImageReceivedAt: camera.last_image_received_at ?? null,
    brokenFrame: camera.broken_frame ?? null,
    keyframeIntervalSeconds: camera.keyframe_interval_seconds ?? null,
    keyframeIntervalFrames: camera.keyframe_interval_frames ?? null,
    keyframeMeasuredAt: camera.keyframe_measured_at ?? null,
    keyframeIntervalIsLowerBound:
      camera.keyframe_interval_is_lower_bound ?? null,
    lastConnectionError: normalizeConnectionError(camera.last_connection_error),
  })),
  errors: body.errors ?? [],
  connectionError: normalizeConnectionError(body.connection_error),
});

export const fetchCameraLiveness = (
  siteId: number,
  sampleData: boolean
): Promise<GapResult<CameraLiveness>> =>
  gapCall(
    `GET customer/${siteId}/camera_liveness/`,
    "Per-camera live signals come from actuate_admin's customer/{id}/camera_liveness/ (ENG-2046); this environment does not have it yet.",
    async () => {
      const response = await apiGet<ApiCameraLiveness>(
        `customer/${siteId}/camera_liveness/`,
        { timeout: 30_000 }
      );
      return mapCameraLiveness(response.data);
    },
    // Only the named scenarios sample it; the mixed mockup reads the real one.
    sampleData ? cameraLivenessSample(siteId) : null
  );

const cameraLivenessSample = (
  siteId: number
): (() => CameraLiveness) | null => {
  const sample = SAMPLE_CAMERA_LIVENESS(siteId, sampleScenario());
  return sample ? () => sample : null;
};

// ---------------------------------------------------------------------------
// "Did we change anything?" — actuate_admin `customer/{id}/changes/` (ENG-1840)
// ---------------------------------------------------------------------------

/** Wire shape of `GET customer/{id}/changes/`, the fields this page reads. */
interface ApiSiteChanges {
  since: string;
  until: string;
  /** Admin capped the settings history (newest 100 saves per key; ENG-2244). */
  truncated?: boolean;
  settings_truncated?: boolean;
  /** When the site last worked (ENG-2244); absent on older admin. */
  markers?: {
    last_alert_at?: string | null;
    last_connected_at?: string | null;
    last_connected_source?: string | null;
  } | null;
  settings_available: boolean;
  settings_versions: {
    version_id: string;
    saved_at: string;
    is_latest: boolean;
    readable: boolean;
    saved_by: string | null;
    previous_version_id: string | null;
    changes: { path: string; from_value?: unknown; to_value?: unknown }[];
  }[];
  admin_changes: {
    at: string;
    user: string | null;
    field: string;
    from_value?: unknown;
    to_value?: unknown;
    type: string;
  }[];
  connector: {
    pod_boot_time: string | null;
    settings_version_id: string | null;
    latest_settings_version_id: string | null;
    latest_settings_saved_at: string | null;
    running_latest: boolean | null;
    deployed_date: string | null;
  };
}

const connectedSource = (
  value: string | null | undefined
): SiteMarkers["lastConnectedSource"] =>
  value === "last_frame" || value === "camera_status" ? value : null;

export const mapSiteChanges = (
  siteId: number,
  windowHours: number,
  data: ApiSiteChanges,
  window?: ChangesWindow
): SiteChanges => ({
  siteId,
  windowHours,
  ...(window ? { window } : {}),
  since: data.since,
  until: data.until,
  truncated: Boolean(data.settings_truncated || data.truncated),
  markers: {
    lastAlertAt: data.markers?.last_alert_at ?? null,
    lastConnectedAt: data.markers?.last_connected_at ?? null,
    lastConnectedSource: connectedSource(data.markers?.last_connected_source),
  },
  settingsAvailable: Boolean(data.settings_available),
  settingsVersions: (data.settings_versions ?? []).map((v) => ({
    versionId: v.version_id,
    savedAt: v.saved_at,
    isLatest: Boolean(v.is_latest),
    readable: v.readable !== false,
    savedBy: v.saved_by ?? null,
    previousVersionId: v.previous_version_id ?? null,
    changes: (v.changes ?? []).map((c) => ({
      path: c.path,
      from: c.from_value ?? null,
      to: c.to_value ?? null,
    })),
  })),
  adminChanges: (data.admin_changes ?? []).map((c) => ({
    at: c.at,
    user: c.user ?? null,
    field: c.field,
    from: c.from_value ?? null,
    to: c.to_value ?? null,
    type: c.type,
  })),
  connector: {
    podBootTime: data.connector?.pod_boot_time ?? null,
    settingsVersionId: data.connector?.settings_version_id ?? null,
    latestSettingsVersionId: data.connector?.latest_settings_version_id ?? null,
    latestSettingsSavedAt: data.connector?.latest_settings_saved_at ?? null,
    runningLatest: data.connector?.running_latest ?? null,
    deployedDate: data.connector?.deployed_date ?? null,
  },
});

/** What we changed on one site over `window` (default the last 24 h). */
export const fetchSiteChanges = (
  siteId: number,
  sampleData: boolean,
  window: ChangesWindow = DEFAULT_CHANGES_WINDOW
): Promise<GapResult<SiteChanges>> =>
  gapCall(
    "GET customer/{id}/changes/",
    "Our change history for a site comes from actuate_admin's customer/{id}/changes/ (ENG-1840); this environment does not have it yet.",
    async () => {
      const response = await apiGet<ApiSiteChanges>(
        `customer/${siteId}/changes/?${changesQuery(window)}`,
        { timeout: 30_000 }
      );
      return mapSiteChanges(
        siteId,
        windowHoursOf(window),
        response.data,
        window
      );
    },
    sampleData
      ? () => ({
          ...SAMPLE_SITE_CHANGES(siteId),
          window,
          windowHours: windowHoursOf(window),
        })
      : null
  );

/** Wire shape of `GET customer/{id}/changes/compare/?at=` (ENG-2244). */
interface ApiSettingsCompare {
  at: string;
  sources: {
    flex_schedule_id?: number | null;
    schedule_name?: string | null;
    settings_key: string;
    baseline?: { version_id: string; saved_at: string } | null;
    current?: { version_id: string; saved_at: string } | null;
    changes?: { path: string; from_value?: unknown; to_value?: unknown }[];
    identical?: boolean;
    reason?: string | null;
  }[];
}

const mapVersionRef = (
  ref: { version_id: string; saved_at: string } | null | undefined
) => (ref ? { versionId: ref.version_id, savedAt: ref.saved_at } : null);

export const mapSettingsCompare = (
  siteId: number,
  data: ApiSettingsCompare
): SettingsCompare => ({
  siteId,
  at: data.at,
  sources: (data.sources ?? []).map((source) => ({
    flexScheduleId: source.flex_schedule_id ?? null,
    scheduleName: source.schedule_name ?? null,
    settingsKey: source.settings_key,
    baseline: mapVersionRef(source.baseline),
    current: mapVersionRef(source.current),
    changes: (source.changes ?? []).map((c) => ({
      path: c.path,
      from: c.from_value ?? null,
      to: c.to_value ?? null,
    })),
    identical: Boolean(source.identical),
    reason: source.reason ?? null,
  })),
});

/**
 * "Compare to when it last worked": the site's settings now against the
 * versions live at `at` (the last alert), per settings key (ENG-2244).
 */
export const fetchSettingsCompare = (
  siteId: number,
  at: string,
  sampleData: boolean
): Promise<GapResult<SettingsCompare>> =>
  gapCall(
    "GET customer/{id}/changes/compare/",
    "Comparing to when a site last worked needs actuate_admin's customer/{id}/changes/compare/ (ENG-2244); this environment does not have it yet.",
    async () => {
      const response = await apiGet<ApiSettingsCompare>(
        `customer/${siteId}/changes/compare/?at=${encodeURIComponent(at)}`,
        { timeout: 30_000 }
      );
      return mapSettingsCompare(siteId, response.data);
    },
    sampleData ? () => SAMPLE_SETTINGS_COMPARE(siteId, at) : null
  );
